import React, { useState } from 'react'

const RoundSuccessScreen = ({ 
  lastMatchResult, 
  currentRound, 
  continueState, 
  onContinueToNextRound 
}) => {
  const [showResults, setShowResults] = useState(true)

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="card-retro p-8 text-center card-glow">
          {/* Result header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4 text-neon-green">
              Common Ground Found 🎉
            </h1>
            <p className="text-purgatory-mist text-lg">
              Completed Round {lastMatchResult.rounds_completed || lastMatchResult.new_level || 0}/3
            </p>
          </div>

          {/* This Round and Running Total */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-neon-green mb-2">
                {Math.round(((lastMatchResult.matches || 0) / 3) * 100)}%
              </div>
              <p className="text-purgatory-mist text-sm">This Round</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-neon-blue mb-2">
                {(() => {
                  const totalMatches = lastMatchResult.game_summary?.total_matches || lastMatchResult.total_matches || lastMatchResult.matches || 0;
                  const totalQuestions = lastMatchResult.game_summary?.total_questions || (lastMatchResult.rounds_completed || 1) * 3;
                  return Math.round((totalMatches / totalQuestions) * 100);
                })()}%
              </div>
              <p className="text-purgatory-mist text-sm">Running Total</p>
            </div>
          </div>

          {/* Your Results - Collapsible Section */}
          <div className="card-retro p-6 mb-8">
            <button
              onClick={() => setShowResults(!showResults)}
              className="w-full text-left flex items-center justify-between"
            >
              <h3 className="text-purgatory-gold font-bold text-lg">
                Your Results
              </h3>
              <div className="text-purgatory-gold text-xl transform transition-transform duration-200">
                {showResults ? '−' : '+'}
              </div>
            </button>
            
            {showResults && (
              <div className="mt-4 space-y-4">
                {/* Round Summaries - Show all rounds */}
                {lastMatchResult.game_summary && lastMatchResult.game_summary.rounds ? (
                  lastMatchResult.game_summary.rounds.map((round, roundIndex) => (
                    <div key={roundIndex} className="mb-6">
                      <div className="text-lg font-bold text-neon-green mb-3">
                        Round {roundIndex + 1} - {Math.round((round.matches_count / 3) * 100)}% Match
                      </div>
                      <div className="space-y-3">
                        {round.questions && round.questions.map((question, qIndex) => (
                          <div key={qIndex} className="bg-purgatory-ash bg-opacity-20 p-3 rounded">
                            <div className="text-sm text-purgatory-mist mb-2">
                              Question {qIndex + 1}
                            </div>
                            <div className="text-sm mb-2">{question.text}</div>
                            <div className="grid grid-cols-2 gap-4 text-xs">
                              <div>
                                <span className="text-purgatory-mist">You: </span>
                                <span className={`font-bold ${
                                  round.match_details && round.match_details[qIndex] ? 'text-neon-green' : 'text-purgatory-ember'
                                }`}>
                                  {question.options && round.user1_answers && question.options[round.user1_answers[qIndex]]}
                                </span>
                              </div>
                              <div>
                                <span className="text-purgatory-mist">Them: </span>
                                <span className={`font-bold ${
                                  round.match_details && round.match_details[qIndex] ? 'text-neon-green' : 'text-purgatory-ember'
                                }`}>
                                  {question.options && round.user2_answers && question.options[round.user2_answers[qIndex]]}
                                </span>
                              </div>
                            </div>
                            <div className="text-center mt-2">
                              {round.match_details && round.match_details[qIndex] ? (
                                <span className="text-neon-green text-xs">✓ MATCH</span>
                              ) : (
                                <span className="text-purgatory-ember text-xs">✗ NO MATCH</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  /* Fallback - show current round only */
                  <div className="space-y-4">
                    <div className="text-lg font-bold text-neon-green mb-3">
                      Round {lastMatchResult.rounds_completed || 1} - {Math.round(((lastMatchResult.matches || 0) / 3) * 100)}% Match
                    </div>
                    <div className="space-y-3">
                      {(lastMatchResult.questions || currentRound.questions).map((question, index) => (
                        <div key={index} className="bg-purgatory-ash bg-opacity-20 p-3 rounded">
                          <div className="text-sm text-purgatory-mist mb-2">
                            Question {index + 1}
                          </div>
                          <div className="text-sm mb-2">{question.text}</div>
                          <div className="grid grid-cols-2 gap-4 text-xs">
                            <div>
                              <span className="text-purgatory-mist">You: </span>
                              <span className={`font-bold ${
                                (lastMatchResult.match_details && lastMatchResult.match_details[index]) ? 'text-neon-green' : 'text-purgatory-ember'
                              }`}>
                                {(lastMatchResult.player1_answers && question.options && question.options[lastMatchResult.player1_answers[index]]) || 'No answer'}
                              </span>
                            </div>
                            <div>
                              <span className="text-purgatory-mist">Them: </span>
                              <span className={`font-bold ${
                                (lastMatchResult.match_details && lastMatchResult.match_details[index]) ? 'text-neon-green' : 'text-purgatory-ember'
                              }`}>
                                {(lastMatchResult.player2_answers && question.options && question.options[lastMatchResult.player2_answers[index]]) || 'No answer'}
                              </span>
                            </div>
                          </div>
                          <div className="text-center mt-2">
                            {(lastMatchResult.match_details && lastMatchResult.match_details[index]) ? (
                              <span className="text-neon-green text-xs">✓ MATCH</span>
                            ) : (
                              <span className="text-purgatory-ember text-xs">✗ NO MATCH</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action buttons based on continue state */}
          <div className="space-y-4">
            {continueState.userClicked && continueState.waitingForPartner ? (
              // User clicked, waiting for partner
              <div className="text-center">
                <div className="text-purgatory-gold text-lg font-bold">
                  ⏳ Waiting for partner to progress...
                </div>
              </div>
            ) : continueState.partnerWaiting ? (
              // Partner is waiting, user needs to click
              <div className="space-y-3">
                <div className="text-center">
                  <div className="text-purgatory-ember text-lg font-bold mb-4 animate-pulse">
                    🔥 Your partner is waiting!
                  </div>
                </div>
                <button 
                  onClick={onContinueToNextRound}
                  className="btn-primary w-full text-lg py-4 bg-neon-green text-purgatory-dark hover:bg-neon-green hover:bg-opacity-90 animate-pulse"
                >
                  Continue to next round
                </button>
              </div>
            ) : (
              // Default state - both need to click
              <button 
                onClick={onContinueToNextRound}
                className="btn-primary w-full text-lg py-4 bg-neon-green text-purgatory-dark hover:bg-neon-green hover:bg-opacity-90"
              >
                Continue to next round
              </button>
            )}
          </div>

          {/* Donate Link */}
          <div className="text-center mt-8">
            <a 
              href="https://checkout.revolut.com/pay/4b87e61e-9c30-43a3-8822-9877bed4fe24" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purgatory-mist text-sm hover:text-purgatory-gold transition-colors underline"
            >
              Donate to support our work
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoundSuccessScreen
